import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Plus, Trash2, TrendingDown, Users, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { calcDilutionForecast } from '@/utils/equityMath';
import type { DilutionStakeholder, DilutionRound } from '@/utils/equityMath';

type Stakeholder = DilutionStakeholder;

interface FundingRound extends DilutionRound {
  stage: string;
}

/**
 * Chart series are keyed by holding id, never by name — two stakeholders sharing
 * a name used to collapse into one series, and one of them disappeared.
 */
const seriesKey = (id: string) => `h_${id}`;

export default function FounderDilutionTracker() {
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([
    { id: '1', name: 'Founder 1', type: 'founder', initialShares: 5000000 },
    { id: '2', name: 'Founder 2', type: 'founder', initialShares: 5000000 },
  ]);
  const [rounds, setRounds] = useState<FundingRound[]>([]);
  const [newStakeholder, setNewStakeholder] = useState<{
    name: string;
    type: Stakeholder['type'];
    shares: number;
  }>({
    name: '',
    type: 'founder',
    shares: 0,
  });
  const [newRound, setNewRound] = useState<Omit<FundingRound, 'id'>>({
    name: '',
    stage: 'Seed',
    newMoney: 0,
    preMoneyValuation: 0,
    optionPoolRefresh: 0,
    poolTiming: 'pre',
  });

  const addStakeholder = () => {
    if (newStakeholder.name && newStakeholder.shares > 0) {
      setStakeholders([
        ...stakeholders,
        { id: Date.now().toString(), ...newStakeholder, initialShares: newStakeholder.shares },
      ]);
      setNewStakeholder({ name: '', type: 'founder', shares: 0 });
    }
  };

  const removeStakeholder = (id: string) => {
    setStakeholders(stakeholders.filter((s) => s.id !== id));
  };

  const addRound = () => {
    if (newRound.name && newRound.newMoney > 0 && newRound.preMoneyValuation > 0) {
      setRounds([...rounds, { id: Date.now().toString(), ...newRound }]);
      setNewRound({
        name: '',
        stage: 'Seed',
        newMoney: 0,
        preMoneyValuation: 0,
        optionPoolRefresh: 0,
        poolTiming: 'pre',
      });
    }
  };

  const removeRound = (id: string) => {
    setRounds(rounds.filter((r) => r.id !== id));
  };

  const forecast = calcDilutionForecast(stakeholders, rounds);
  const snapshots = forecast.snapshots;

  // Prepare chart data, keyed by holding id.
  const chartData = snapshots.map((snapshot) => {
    const data: Record<string, string | number> = { round: snapshot.roundName };
    snapshot.holdings.forEach((holding) => {
      data[seriesKey(holding.id)] = Number(holding.ownership.toFixed(2));
    });
    data['Option Pool'] = Number(snapshot.optionPoolPct.toFixed(2));
    return data;
  });

  // Every holding that appears at any point, in first-seen order, so a line is
  // drawn for each even though later rounds introduce new ones.
  const allHoldings: { id: string; name: string }[] = [];
  snapshots.forEach((s) =>
    s.holdings.forEach((h) => {
      if (!allHoldings.some((x) => x.id === h.id)) allHoldings.push({ id: h.id, name: h.name });
    })
  );

  const colors = [
    'hsl(var(--primary))',
    'hsl(var(--secondary))',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7c7c',
    '#a78bfa',
    '#fb923c',
  ];

  // Retention, matched by id so founders sharing a name are tracked separately.
  const ownershipOf = (snapshot: (typeof snapshots)[number] | undefined, id: string) =>
    snapshot?.holdings.find((h) => h.id === id)?.ownership ?? 0;

  const founderRetention = stakeholders
    .filter((s) => s.type === 'founder')
    .map((founder) => {
      const initialPct = ownershipOf(snapshots[0], founder.id);
      const finalPct = ownershipOf(snapshots[snapshots.length - 1], founder.id);
      return {
        id: founder.id,
        name: founder.name,
        initial: initialPct,
        final: finalPct,
        retention: initialPct > 0 ? (finalPct / initialPct) * 100 : 0,
      };
    });

  const totalDilution =
    founderRetention.length > 0
      ? founderRetention.reduce((sum, f) => sum + (100 - f.retention), 0) / founderRetention.length
      : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5" />
            Multi-Stage Dilution Forecaster
          </CardTitle>
          <CardDescription>
            Model founder and employee ownership across multiple funding rounds with option pool
            management
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {forecast.warnings.length > 0 && (
            <div className="space-y-2">
              {forecast.warnings.map((warning, i) => (
                <div
                  key={i}
                  className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                  <p className="text-sm text-muted-foreground">{warning}</p>
                </div>
              ))}
            </div>
          )}

          {/* Initial Stakeholders */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <h3 className="font-semibold">Initial Stakeholders</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newStakeholder.name}
                  onChange={(e) => setNewStakeholder({ ...newStakeholder, name: e.target.value })}
                  placeholder="Stakeholder name"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={newStakeholder.type}
                  onValueChange={(value: Stakeholder['type']) =>
                    setNewStakeholder({ ...newStakeholder, type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="founder">Founder</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="investor">Early Investor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Initial Shares</Label>
                <Input
                  type="number"
                  value={newStakeholder.shares || ''}
                  onChange={(e) =>
                    setNewStakeholder({
                      ...newStakeholder,
                      shares: Math.max(0, Number(e.target.value)),
                    })
                  }
                  placeholder="1000000"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={addStakeholder} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {stakeholders.map((stakeholder) => (
                <div
                  key={stakeholder.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant="outline">{stakeholder.type}</Badge>
                    <span className="font-medium">{stakeholder.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {stakeholder.initialShares.toLocaleString()} shares
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStakeholder(stakeholder.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Funding Rounds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <h3 className="font-semibold">Funding Rounds</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <Label>Round Name</Label>
                <Input
                  value={newRound.name}
                  onChange={(e) => setNewRound({ ...newRound, name: e.target.value })}
                  placeholder="Series A"
                />
              </div>
              <div>
                <Label>Stage</Label>
                <Select
                  value={newRound.stage}
                  onValueChange={(value) => setNewRound({ ...newRound, stage: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Seed">Seed</SelectItem>
                    <SelectItem value="Series A">Series A</SelectItem>
                    <SelectItem value="Series B">Series B</SelectItem>
                    <SelectItem value="Series C">Series C</SelectItem>
                    <SelectItem value="Series D">Series D</SelectItem>
                    <SelectItem value="Series E+">Series E+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New Money ($M)</Label>
                <Input
                  type="number"
                  value={newRound.newMoney || ''}
                  onChange={(e) =>
                    setNewRound({ ...newRound, newMoney: Math.max(0, Number(e.target.value)) })
                  }
                  min="0"
                  placeholder="5000000"
                />
              </div>
              <div>
                <Label>Pre-Money Val ($M)</Label>
                <Input
                  type="number"
                  value={newRound.preMoneyValuation || ''}
                  onChange={(e) =>
                    setNewRound({
                      ...newRound,
                      preMoneyValuation: Math.max(0, Number(e.target.value)),
                    })
                  }
                  placeholder="15000000"
                />
              </div>
              <div>
                <Label>Pool Refresh (%)</Label>
                <Input
                  type="number"
                  value={newRound.optionPoolRefresh || ''}
                  onChange={(e) =>
                    setNewRound({
                      ...newRound,
                      optionPoolRefresh: Math.min(99, Math.max(0, Number(e.target.value))),
                    })
                  }
                  placeholder="10"
                />
              </div>
              <div>
                <Label>Pool Timing</Label>
                <Select
                  value={newRound.poolTiming}
                  onValueChange={(value: FundingRound['poolTiming']) =>
                    setNewRound({ ...newRound, poolTiming: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre">Pre-Money</SelectItem>
                    <SelectItem value="post">Post-Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={addRound} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Funding Round
            </Button>
            <div className="space-y-2">
              {rounds.map((round) => (
                <div
                  key={round.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge>{round.stage}</Badge>
                    <span className="font-medium">{round.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ${(round.newMoney / 1000000).toFixed(1)}M @ $
                      {(round.preMoneyValuation / 1000000).toFixed(1)}M pre
                    </span>
                    {round.optionPoolRefresh > 0 && (
                      <Badge variant="secondary">
                        +{round.optionPoolRefresh}% pool ({round.poolTiming}-money)
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeRound(round.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {snapshots.length > 1 && (
        <>
          {/* Retention Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Founder Retention Analysis</CardTitle>
              <CardDescription>How much equity founders retain through dilution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Average Dilution</div>
                  <div className="text-2xl font-bold text-destructive">
                    {totalDilution.toFixed(1)}%
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Funding Rounds</div>
                  <div className="text-2xl font-bold">{rounds.length}</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Final Option Pool</div>
                  <div className="text-2xl font-bold">
                    {snapshots[snapshots.length - 1].optionPoolPct.toFixed(1)}%
                  </div>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Founder</TableHead>
                    <TableHead>Initial %</TableHead>
                    <TableHead>Final %</TableHead>
                    <TableHead>Retention Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {founderRetention.map((f) => (
                    <TableRow key={f.name}>
                      <TableCell className="font-medium">{f.name}</TableCell>
                      <TableCell>{f.initial.toFixed(2)}%</TableCell>
                      <TableCell>{f.final.toFixed(2)}%</TableCell>
                      <TableCell>
                        <Badge variant={f.retention >= 50 ? 'default' : 'destructive'}>
                          {f.retention.toFixed(1)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Ownership Evolution Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Ownership Evolution</CardTitle>
              <CardDescription>How equity ownership changes across funding rounds</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="round" />
                  <YAxis label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {allHoldings.map((holding, idx) => (
                    <Line
                      key={holding.id}
                      type="monotone"
                      dataKey={seriesKey(holding.id)}
                      name={holding.name}
                      stroke={colors[idx % colors.length]}
                      strokeWidth={2}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="Option Pool"
                    stroke="#94a3b8"
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detailed Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Round-by-Round Breakdown</CardTitle>
              <CardDescription>Detailed ownership percentages at each stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Stakeholder</TableHead>
                      {snapshots.map((s) => (
                        <TableHead key={s.roundName}>{s.roundName}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allHoldings.map((holding) => (
                      <TableRow key={holding.id}>
                        <TableCell className="font-medium">{holding.name}</TableCell>
                        {snapshots.map((s) => {
                          const held = s.holdings.find((h) => h.id === holding.id);
                          return (
                            <TableCell key={s.roundId}>
                              {held && held.ownership > 0 ? `${held.ownership.toFixed(2)}%` : '-'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell className="font-medium">Option Pool</TableCell>
                      {snapshots.map((s) => (
                        <TableCell key={s.roundName}>
                          {s.optionPoolPct > 0 ? `${s.optionPoolPct.toFixed(2)}%` : '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Retention Strategy Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {totalDilution > 70 && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium text-destructive">⚠️ High Dilution Warning</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Founders experiencing {totalDilution.toFixed(1)}% average dilution. Consider
                    negotiating for anti-dilution protection or lower valuations with less capital
                    raised.
                  </p>
                </div>
              )}
              {rounds.some((r) => r.poolTiming === 'pre') && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-sm font-medium">💡 Pre-Money Pool Strategy</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Pre-money option pools dilute existing shareholders before new investors.
                    Consider negotiating for post-money pools to reduce founder dilution.
                  </p>
                </div>
              )}
              {snapshots[snapshots.length - 1].optionPoolPct > 15 && (
                <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm font-medium">📊 Large Option Pool</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Final option pool is {snapshots[snapshots.length - 1].optionPoolPct.toFixed(1)}
                    %. Ensure hiring plans justify this size to avoid unnecessary dilution.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
